<template>
  <section v-if="firstPhotos.length || secondPhotos.length" class="ni-section ni-recommended">
    <div class="ni-container ni-container--wide">
      <SectionHeader subtitle="TOP RECOMMENDED">
        <template #title>
          <span class="ni-section-header__title-mark">{{ t('index.hot') }}</span>
        </template>
      </SectionHeader>
      <div :ref="setListRef" class="ni-recommended__list">
        <div v-if="firstPhotos.length" class="ni-recommended__row ni-recommended__row--first" :style="rowStyle">
          <RouterLink
            v-for="(photo, index) in firstPhotos"
            :key="photo.id || index"
            class="ni-recommended__photo"
            :style="firstPhotoStyle(index)"
            :to="buildPhotoDetailUrl(photo.id)"
          >
            <img :src="photo.preview || photo.thumb" :alt="photo.title || ''" loading="lazy" @contextmenu.prevent />
          </RouterLink>
        </div>
        <div v-if="secondPhotos.length" class="ni-recommended__row ni-recommended__row--second" :style="rowStyle">
          <RouterLink
            v-for="(photo, index) in secondPhotos"
            :key="photo.id || index"
            class="ni-recommended__photo"
            :class="{ 'ni-recommended__photo--tall': photo.first }"
            :style="secondPhotoStyle(index)"
            :to="buildPhotoDetailUrl(photo.id)"
          >
            <img :src="photo.preview || photo.thumb" :alt="photo.title || ''" loading="lazy" @contextmenu.prevent />
          </RouterLink>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { buildPhotoDetailUrl } from '@/utils/seoUrl.js';
import SectionHeader from './shared/SectionHeader.vue';

defineProps({
  firstPhotos: { type: Array, default: () => [] },
  secondPhotos: { type: Array, default: () => [] },
});

const LEFT_PART = 26.5;
const RIGHT_PART = 9.4;
const TOTAL_PART = LEFT_PART + RIGHT_PART;

const { t } = useI18n();
const listRef = ref(null);
const layoutWidth = ref(0);
const isMobile = ref(false);
let resizeObserver;

function setListRef(el) {
  listRef.value = el;
  if (resizeObserver) resizeObserver.disconnect();
  if (!el) return;
  updateLayout();
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(el);
  }
}

function updateLayout() {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches;
  layoutWidth.value = listRef.value ? listRef.value.clientWidth : 0;
}

onMounted(() => {
  updateLayout();
  if (typeof ResizeObserver === 'undefined') {
    window.addEventListener('resize', updateLayout);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  window.removeEventListener('resize', updateLayout);
});

const rowStyle = computed(() => {
  if (!layoutWidth.value) return {};
  if (isMobile.value) return { width: '100%', height: 'auto' };
  const height = (layoutWidth.value * 16) / TOTAL_PART;
  return { height: `${height}px` };
});

function firstPhotoStyle(index) {
  if (!layoutWidth.value) return {};
  if (isMobile.value) return { width: '100%', height: 'auto' };
  const per = layoutWidth.value / TOTAL_PART;
  if (index === 0) {
    const width = LEFT_PART * per;
    return { width: `${width}px`, height: `${(width * 16) / LEFT_PART}px` };
  }
  if (index === 1) {
    const width = RIGHT_PART * per;
    return { width: `${width}px`, height: `${(width * 16) / RIGHT_PART}px` };
  }
  return {};
}

function secondPhotoStyle(index) {
  if (!layoutWidth.value) return {};
  if (isMobile.value) return { width: '100%', height: 'auto' };
  const firstWidth = RIGHT_PART * (layoutWidth.value / TOTAL_PART);
  if (index === 0) return { width: `${firstWidth}px` };
  return { width: `${(layoutWidth.value - firstWidth) / 2}px` };
}
</script>

<style scoped>
.ni-recommended :deep(.ni-section-header__bar) {
  display: none;
}

.ni-recommended__list {
  width: 100%;
  padding: 20px 0;
}

.ni-recommended__row {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 35.9 / 16;
}

.ni-recommended__photo {
  position: relative;
  display: block;
  overflow: hidden;
  padding: 8px;
  border-radius: 8px;
  box-sizing: border-box;
}

.ni-recommended__row--first .ni-recommended__photo {
  height: 100%;
}

.ni-recommended__row--second .ni-recommended__photo {
  width: 33.3%;
  height: 50%;
}

.ni-recommended__row--second .ni-recommended__photo--tall {
  height: 100%;
}

.ni-recommended__photo img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .ni-recommended__row {
    aspect-ratio: auto;
    flex-wrap: nowrap;
    height: auto;
  }

  .ni-recommended__photo,
  .ni-recommended__row--first .ni-recommended__photo,
  .ni-recommended__row--second .ni-recommended__photo,
  .ni-recommended__row--second .ni-recommended__photo--tall {
    width: 100%;
    height: auto;
    padding: 5px;
  }

  .ni-recommended__photo img {
    width: 100%;
    height: 100%;
  }
}
</style>
